import React from 'react';
import { PrismicPreviewProvider } from 'gatsby-plugin-prismic-previews';

// GATSBY_CMS_REPOSITORY is derived from CMS_REPOSITORY in gatsby-config.js.
const repositoryName = process.env.GATSBY_CMS_REPOSITORY;

export const repositoryConfigs = [{ repositoryName }];

export const wrapRootElement = ({ element }) => (
  <PrismicPreviewProvider repositoryConfigs={repositoryConfigs}>{element}</PrismicPreviewProvider>
);

const PREVIEW_COOKIE_NAME = 'io.prismic.preview';
const PREVIEW_DOCUMENT_ID_KEY = 'io.prismic.preview.documentId';
const TOOLBAR_SCRIPT_KEY = `prismic-toolbar-${repositoryName}`;

// The same plugin adds a preconnect and a dns-prefetch for the toolbar's origin
// (static.cdn.prismic.io) to the head of every page. Keys are from the plugin's
// own gatsby-ssr/onRenderBody.
const TOOLBAR_HINT_KEYS = ['preconnect-prismic-toolbar', 'dns-prefetch-prismic-toolbar'];

/**
 * Removes the Prismic toolbar script that gatsby-plugin-prismic-previews injects on every page.
 *
 * The toolbar owns the `io.prismic.preview` cookie. On load it asks an iframe on
 * <repo>.prismic.io for the current preview session; on a custom domain that is a third-party
 * request, so it comes back empty and the toolbar concludes there is no preview: it deletes the
 * cookie and calls window.location.reload(). That happens while the plugin is still fetching, so
 * the reloaded page has no ref, renders published content, and — because the resolver already
 * dropped the token from the URL — has no way to recover. Reloading by hand does not help either,
 * which is exactly how this presented.
 *
 * The site does not need the toolbar: the ref comes from the preview URL's token (see
 * persistPreviewTokenFromUrl) and the plugin reads it from the cookie directly. Its only other job
 * is the in-page edit overlay, which is not worth previews never working. The plugin injects it
 * unconditionally with no option to opt out, so drop the component it adds.
 *
 * The two resource hints for the same origin go with it.
 *
 * A preconnect is not a fetch, but it is not free either: the browser opens a real TCP and TLS
 * connection to static.cdn.prismic.io on every page view, which discloses the visitor's IP address
 * to a US host. That is the transfer the /cms-image/ proxy was built to remove, so leaving the
 * hints in place would keep a quieter version of it. With the toolbar script stripped above,
 * nothing is ever fetched from that origin, so the hints only cost a connection.
 */
export const onPreRenderHTML = ({
  getPostBodyComponents,
  replacePostBodyComponents,
  getHeadComponents,
  replaceHeadComponents,
}) => {
  replacePostBodyComponents(
    getPostBodyComponents().filter(component => component?.key !== TOOLBAR_SCRIPT_KEY),
  );

  replaceHeadComponents(
    getHeadComponents().filter(component => !TOOLBAR_HINT_KEYS.includes(component?.key)),
  );
};

const readPreviewCookie = () => document.cookie
  .split('; ')
  .find(entry => entry.startsWith(`${PREVIEW_COOKIE_NAME}=`));

// The token is written to a cookie and then used as the API ref, so only accept a value that
// really is a preview URL on our own repository. Semicolons and commas would also split the
// cookie value.
const isOwnPreviewToken = token => {
  if (/[;,]/.test(token)) return false;

  try {
    const { protocol, hostname, pathname } = new URL(token);

    return (
      protocol === 'https:'
      && hostname === `${repositoryName}.prismic.io`
      && pathname.startsWith('/previews/')
    );
  } catch (error) {
    return false;
  }
};

// Prismic document ids are short base64url strings. Checked before going into a query predicate.
const isDocumentId = value => /^[\w-]{6,64}$/.test(value);

// Matches the plugin's own isReleasePreview: a release ref carries a `~` before the query string.
const isReleasePreviewCookie = cookie => /\/previews\/.*~.*\?websitePreviewId/
  .test(decodeURIComponent(cookie));

/**
 * Stores the preview ref from the URL in the cookie the plugin and @prismicio/client both read.
 *
 * Prismic links straight to /preview?token=…&documentId=… and cannot set a cookie on our domain,
 * so normally the only thing that writes it is the deferred, cross-origin toolbar script. That
 * loses a race against hydration: the plugin checks the cookie once in a mount effect, finds
 * nothing, and never retries. Writing it here makes the ref available before React renders.
 */
export const persistPreviewTokenFromUrl = () => {
  if (typeof document === 'undefined' || !repositoryName) return false;

  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');
  if (!token || !isOwnPreviewToken(token)) return false;

  // Same shape the toolbar writes: the plugin matches on it to find the active repository, and
  // the Content API unwraps it to get the ref.
  const value = JSON.stringify({ [`${repositoryName}.prismic.io`]: { preview: token } });
  document.cookie = `${PREVIEW_COOKIE_NAME}=${value}; path=/; SameSite=Lax`;

  // The resolver navigates away from /preview, dropping documentId from the URL, but the plugin
  // bootstraps on the destination page. Keep the id for the rest of the tab's preview session.
  const documentId = params.get('documentId');
  if (documentId && isDocumentId(documentId)) {
    try {
      window.sessionStorage.setItem(PREVIEW_DOCUMENT_ID_KEY, documentId);
    } catch (error) {
      // Storage can be unavailable; narrowing is an enhancement, so carry on without it.
    }
  }

  return true;
};

/**
 * Restricts the plugin's bootstrap query to the document actually being previewed.
 *
 * gatsby-plugin-prismic-previews loads the preview session's content with a bare
 * `client.getFirst()` — no filter, no ordering (see lib/fetchNewDocuments). Prismic returns
 * documents in an arbitrary order, so that lands on whichever document the repository happens to
 * list first rather than the previewed one. The page then looks itself up by `_previewable`,
 * misses, and falls back to rendering published content, which is indistinguishable from previews
 * simply not working. The plugin never uses the `documentId` Prismic puts in the preview URL.
 *
 * Until that is fixed upstream (6.0.2 is the latest release) the only seam from outside the plugin
 * is the request itself: its store and document normalizer are not exported. So add the missing
 * filter to that one query. It is identifiable because it is the only search the plugin issues
 * without a `q`; everything else looks documents up by id. If the query ever stops matching, this
 * does nothing and behaviour is exactly as it is today.
 */
export const narrowPreviewBootstrapToDocument = () => {
  if (typeof window === 'undefined' || !repositoryName) return false;

  const cookie = readPreviewCookie();
  if (!cookie) return false;

  // Release previews cover many documents at once and the plugin fetches them as a set, keyed off
  // whatever getFirst returns. Narrowing to one document would break that, so leave them alone.
  if (isReleasePreviewCookie(cookie)) return false;

  let documentId = null;
  try {
    documentId = window.sessionStorage.getItem(PREVIEW_DOCUMENT_ID_KEY);
  } catch (error) {
    return false;
  }
  if (!documentId || !isDocumentId(documentId)) return false;

  // Bound: fetch throws "Illegal invocation" if called with anything but window as its receiver.
  const originalFetch = window.fetch.bind(window);

  window.fetch = (input, init) => {
    // Only rewrite plain URLs. A Request carries its own body and headers that reconstructing the
    // call would drop, and the Prismic client never uses one.
    if (typeof input !== 'string' && !(input instanceof URL)) return originalFetch(input, init);

    let url;
    try {
      url = new URL(String(input), window.location.href);
    } catch (error) {
      return originalFetch(input, init);
    }

    const isBootstrapQuery = url.hostname.endsWith('.prismic.io')
      && url.pathname.endsWith('/documents/search')
      && !url.searchParams.has('q');

    if (!isBootstrapQuery) return originalFetch(input, init);

    url.searchParams.set('q', `[[at(document.id, "${documentId}")]]`);
    window.fetch = originalFetch;

    return originalFetch(url.toString(), init);
  };

  return true;
};
